import {
  Badge,
  Box,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  MenuItem,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  TableContainer
} from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import dayjs from "dayjs";
import axios from "axios";
import { useMemo, useState, useEffect } from "react";
import { SLEDGEHAMMER_BASE_API, SLEDGEHAMMER_NETSUITE_ACCOUNT } from "../config";
import { parseDDMMYYYY, formatDDMMYYYY, dateSortingFn } from "../utils/dateUtils";

const filterByFieldEmpty = (field) => (po) => {
  const val = po && po[field];
  return val === null || val === undefined || val === '-' || (typeof val === 'string' && val.trim() === '');
};

const MILESTONE_DATE_CONFIG = [
  { actual: 'artworkActual', calc: 'artworkCalc', header: 'ACTUAL ARTWORK', calcHeader: 'ARTWORK - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL ARTWORK ' },
  { actual: 'midprodActual', calc: 'midprodCalc', header: 'ACTUAL MID-PRODUCTION CHECK-IN', calcHeader: 'MIDPROD CHECK - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL MID-PRODUCTION CHECK-IN' },
  { actual: 'prodCompleteActual', calc: 'prodCompleteCalc', header: 'ACTUAL PRODUCTION COMPLETION', calcHeader: 'PROD COMPLETE - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL PRODUCTION COMPLETION' },
  { actual: 'inspectionApprovedActual', calc: 'inspectionApprovedCalc', header: 'ACTUAL INSPECTION APPROVED', calcHeader: 'INSPECTION APPROVED - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL INSPECTION APPROVED' },
  { actual: 'crdActual', calc: 'crdCalc', header: 'ACTUAL CARGO READY', calcHeader: 'CRD CONFIRMED - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL CARGO READY DATE' },
  { actual: 'pendingPtsActual', calc: 'pendingPtsCalc', header: 'ACTUAL PTS APPROVAL DATE', calcHeader: 'PTS APPROVAL DATE - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL PTS APPROVAL DATE' },
  { actual: 'rtsActual', calc: 'rtsCalc', header: 'ACTUAL READY TO SHIP DATE', calcHeader: 'READY TO SHIP DATE - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL READY TO SHIP DATE' },
  { actual: 'freightBookedActual', calc: 'freightBookedCalc', header: 'ACTUAL FREIGHT BOOKING DATE', calcHeader: 'FREIGHT BOOKED - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL FREIGHT BOOKING DATE' },
  { actual: 'etdActual', calc: 'etdCalc', header: 'ACTUAL ETD', calcHeader: 'ETD - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL ETD' },
  { actual: 'etaPortActual', calc: 'etaPortCalc', header: 'ACTUAL ETA PORT', calcHeader: 'ETA - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL ETA PORT' },
  { actual: 'lfdPortActual', calc: 'lfdPortCalc', header: 'ACTUAL LFD', calcHeader: 'LFD PORT - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL LFD' },
  { actual: 'arrival3PLActual', calc: 'arrival3PLCalc', header: 'ACTUAL 3PL ARRIVAL DATE', calcHeader: '3PL ARRIVAL - ROLLING DEADLINE (CALC)', popupHeader: 'ACTUAL 3PL ARRIVAL DATE' },
];

const milestoneDateFields = MILESTONE_DATE_CONFIG.map((c) => c.actual);
const milestonePopupHeaders = MILESTONE_DATE_CONFIG.map((c) => c.popupHeader);
const milestoneDateColumns = MILESTONE_DATE_CONFIG.flatMap(({ actual, calc, header, calcHeader }) => [
  { accessorKey: actual, header },
  { accessorKey: calc, header: calcHeader },
]);
const calcToActualMap = Object.fromEntries(
  MILESTONE_DATE_CONFIG.map(({ actual, calc }) => [calc, actual])
);
const calcDateFields = MILESTONE_DATE_CONFIG.map((c) => c.calc);
const actualDates = ['createdDate', 'depositDate', ...milestoneDateFields];
const popupDateGroups = [
  milestoneDateFields.slice(0, 4),
  milestoneDateFields.slice(4, 8),
  ['shipNum'],
  milestoneDateFields.slice(8),
];
const popupTableHeaders = [
  milestonePopupHeaders.slice(0, 4),
  milestonePopupHeaders.slice(4, 8),
  ['INBOUND SHIPMENT ID'],
  milestonePopupHeaders.slice(8),
];
const rtsColumnBackground = 'grey.100';

const FREEZEABLE_COLUMNS = [
  { id: 'action', label: 'Action' },
  { id: 'tranid', label: 'PO' },
  { id: 'shipNum', label: 'Inbound Shipment' },
  { id: 'sku', label: 'SKU' },
  { id: 'qtyDisplay', label: 'Item Quantity / Total Quantity' },
  { id: 'class', label: 'Brand' },
  { id: 'status', label: 'Status' },
  { id: 'entity', label: 'Vendor' },
  { id: 'owner', label: 'Owner' },
  { id: 'countryCode', label: 'Ship Country' },
];
const FREEZEABLE_COLUMN_IDS = FREEZEABLE_COLUMNS.map((c) => c.id);
const DEFAULT_FROZEN_COLUMNS = ['action', 'tranid', 'shipNum', 'sku', 'qtyDisplay'];

const orderFrozenColumns = (selectedIds) =>
  FREEZEABLE_COLUMN_IDS.filter((id) => selectedIds.includes(id));


const POTable = ({
  poData,
  statusTabs,
  terms,
  setPoData,
  enqueueSnackbar,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const baseUrl = SLEDGEHAMMER_BASE_API;
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [specialFilter, setSpecialFilter] = useState(null);
  const [refreshTable, setRefreshTable] = useState(0);
  const [highlightedField, setHighlightedField] = useState(null);
  const [columnPinning, setColumnPinning] = useState({
    left: DEFAULT_FROZEN_COLUMNS,
    right: [],
  });

  const resetSpecialFilter = () => {
    setSpecialFilter(null);
    setHighlightedField(null);
    setRefreshTable(prev => prev + 1);
  };

  const tabCounts = useMemo(() => {
    const counts = { All: poData.length };
    statusTabs.forEach((status) => {
      counts[status] = poData.filter((po) => po.status === status).length;
    });
    return counts;
  }, [poData, statusTabs]);

  const statusActionButtons = {
    "PENDING DEPOSIT": [
      { label: "All", action: resetSpecialFilter },
      { label: "Pending Deposit", action: () => handleButtonFilter('depositDate') },
    ],
    "IN PRODUCTION": [
      { label: "All", action: resetSpecialFilter },
      { label: "Pending Artwork", action: () => handleButtonFilter('artworkActual') },
      { label: "Mid-Production Check-In", action: () => handleButtonFilter('midprodActual') },
      { label: "Pending Production Completion", action: () => handleButtonFilter('prodCompleteActual') },
      { label: "Pending Inspection Approval", action: () => handleButtonFilter('inspectionApprovedActual') },
    ],
    "PRODUCTION COMPLETE": [
      { label: "All", action: resetSpecialFilter },
      { label: "Pending Cargo Ready", action: () => handleButtonFilter('crdActual') },
    ],
    "AWAITING SHIPMENT": [
      { label: "All", action: resetSpecialFilter },
      { label: "Pending PTS Approval", action: () => handleButtonFilter('pendingPtsActual') },
      { label: "Pending RTS", action: () => handleButtonFilter('rtsActual') },
      { label: "Pending Freight", action: () => handleButtonFilter('freightBookedActual') },
    ],
    "ON THE WATER": [
      { label: "All", action: resetSpecialFilter },
      { label: "Shipment ETD", action: () => handleButtonFilter('etdActual') },
      { label: "Pending ETA Port", action: () => handleButtonFilter('etaPortActual') },
    ],
    "AT PORT": [
      { label: "All", action: resetSpecialFilter },
      { label: "Pending LFD", action: () => handleButtonFilter('lfdPortActual') },
      { label: "Pending 3PL Arrival", action: () => handleButtonFilter('arrival3PLActual') },
    ],
    "RECEIPTING AT 3PL": [
      { label: "All", action: resetSpecialFilter },
      { label: "Pending COGS", action: () => handleButtonFilter('pendingCogs') },
    ],
  };

  const readOnlyDateColumns = ['createdDate', 'vendorTargetDate'];
  const rtsDateColumns = ['rtsActual', 'rtsCalc'];
  const columnDividerSx = {
    borderLeft: '3px solid',
    borderLeftColor: 'grey.600',
  };
  const dividerBeforeColumns = new Set(['artworkActual', 'freightBookedActual']);

  const handleButtonFilter = (field) => {
    setHighlightedField(field);
    if (field === 'pendingCogs') {
      setSpecialFilter(() => (po) => !!po.pendingCogs);
    } else {
      setSpecialFilter(() => filterByFieldEmpty(field));
    }
    setRefreshTable(prev => prev + 1);
  };

  useEffect(() => {
    setSpecialFilter(null);
  }, [selectedTab]);

  const owners = useMemo(() => {
    return [...new Set(poData.map(po => po.owner).filter(Boolean))].sort();
  }, [poData]);

  const handleOwnerFilterChange = (event) => {
    const {
      target: { value },
    } = event;
    setOwnerFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handleFrozenColumnsChange = (event) => {
    const {
      target: { value },
    } = event;
    const selected = typeof value === 'string' ? value.split(',') : value;
    setColumnPinning((prev) => ({
      ...prev,
      left: orderFrozenColumns(selected),
    }));
  };

  const frozenColumnSelection = useMemo(
    () => columnPinning.left.filter((id) => FREEZEABLE_COLUMN_IDS.includes(id)),
    [columnPinning.left]
  );

  const handleChange = (field, value) => {
    setEditableData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const parseDate = parseDDMMYYYY;
  const formatDate = formatDDMMYYYY;

  const formatDateForDisplay = (dateValue) => {
    if (!dateValue || dateValue === '-') return '-';
    if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) return dateValue;
    const parsed = parseDDMMYYYY(dateValue);
    if (parsed) return formatDDMMYYYY(parsed);
    const dayjsDate = dayjs(dateValue);
    return dayjsDate.isValid() ? dayjsDate.format('DD/MM/YYYY') : '-';
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const { _rowId, ...lineData } = editableData;
      const payload = {
        ...lineData,
        termsText: terms[editableData.termsText] || null,
      };

      const { data: updated } = await axios.post(`${baseUrl}/dashboard/po`, payload);

      if (updated.success) {
        const updatedData = updated.data ?? editableData;
        const lineSpecificFields = new Set([
          'item', 'itemText', 'sku', 'poQty', 'qtyDisplay', 'rate',
          'fxAmount', 'inboundQty', 'lineProgressStatus', 'lineProgressStatusId', 'lineStatus',
        ]);
        const poLevelData = Object.fromEntries(
          Object.entries(updatedData).filter(([key]) => !lineSpecificFields.has(key))
        );

        const shipmentFields = new Set([
          'etdActual', 'etaPortActual', 'lfdPortActual', 'arrival3PLActual',
        ]);
        const shipmentData = Object.fromEntries(
          Object.entries(updatedData).filter(([key]) => shipmentFields.has(key))
        );

        setPoData((prev) =>
          prev.map((row) => {
            if (row._rowId === editableData._rowId) {
              return { ...row, ...updatedData };
            }
            if (editableData.isRushOrder && row.tranid === editableData.tranid) {
              return { ...row, ...poLevelData };
            }
            if (editableData.inboundId && row.inboundId === editableData.inboundId) {
              return { ...row, ...shipmentData };
            }
            return row;
          })
        );
        setOpenModal(false);
        enqueueSnackbar(`${editableData.tranid} ${updated.message}`, { variant: "success" });
      } else {
        enqueueSnackbar(updated.message, { variant: "error" });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const filteredData = useMemo(() => {
    return poData.filter((po) => {
      const statusMatch = selectedTab === 0 || po.status === statusTabs[selectedTab - 1];
      const ownerMatch = ownerFilter.length === 0 || ownerFilter.includes(po.owner);
      const specialMatch = typeof specialFilter === 'function' ? specialFilter(po) : true;
      return statusMatch && ownerMatch && specialMatch;
    });
  }, [poData, selectedTab, statusTabs, ownerFilter, specialFilter]);

  const columns = useMemo(() => {
    const isDateInPast = (dateStr) => {
      if (!dateStr || dateStr === '-') return false;
      const date = parseDate(dateStr);
      if (!date) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    };

    return [
    {
      accessorKey: "action",
      header: "Action",
      size: 20,
      Cell: ({ row }) => (
          <IconButton onClick={() => {
            setEditableData({ ...row.original, isRushOrder: row.original.isRushOrder ?? false });
            setOpenModal(true);
          }}>
          <EditIcon />
        </IconButton>
      )
    },
    {
      accessorKey: "tranid",
      header: "PO",
      Cell: ({ row }) => {
        const poId = row.original.poInternalId;
        const tranid = row.original.tranid;
        const poUrl = `https://${SLEDGEHAMMER_NETSUITE_ACCOUNT}.app.netsuite.com/app/accounting/transactions/purchord.nl?id=${poId}&whence=`;
        return (
          <a href={poUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>{tranid}</a>
        );
      }
    },
    {
      // Explicit accessor so global search operates on the visible shipment number
      id: "shipNum",
      accessorFn: (row) => (row?.shipNum ? String(row.shipNum).trim() : ""),
      header: "Inbound Shipment",
      Cell: ({ row }) => {
        const inboundId = row.original.inboundId;
        const shipNum = row.original.shipNum?.trim();
        const inboundUrl = `https://${SLEDGEHAMMER_NETSUITE_ACCOUNT}.app.netsuite.com/app/accounting/transactions/shipping/inboundshipment/inboundshipment.nl?id=${inboundId}`;
        return (
          <a href={inboundUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>{shipNum}</a>
        );
      }
    },
    { accessorKey: "sku", header: "SKU" },
    { accessorKey: "qtyDisplay", header: "Item Quantity / Total Quantity" },
    { accessorKey: "class", header: "Brand" },
    { accessorKey: "status", header: "Status" },
    { accessorKey: "entity", header: "Vendor" },
    { accessorKey: "owner", header: "Owner" },
    { accessorKey: "countryCode", header: "Ship Country" },
    {
      accessorKey: "rate",
      header: "Rate",
      size: 100,
      Cell: ({ cell }) => {
        const raw = cell.getValue();
        if (raw == null) return "";

        const str = String(raw).trim();

        // Expect values like "18.51516448 EUR"
        const parts = str.split(/\s+/);
        if (parts.length === 2 && !Number.isNaN(Number(parts[0]))) {
          const amount = Number(parts[0]);
          const currency = parts[1];
          return `${currency} ${amount.toFixed(2)}`;
        }
        // As a last resort, return the original string
        return str;
      },   
    },
    { accessorKey: "termsText", header: "TERMS" },
    {
      accessorKey: "createdDate",
      header: "CREATED",
      Cell: ({ cell }) => formatDateForDisplay(cell.getValue()),
    },
    {
      accessorKey: "vendorTargetDate",
      header: "VENDOR TARGET DATE",
      Cell: ({ cell }) => formatDateForDisplay(cell.getValue()),
    },
    { accessorKey: "depositDate", header: "PO DEPOSIT" },
    ...milestoneDateColumns,
    {
      accessorKey: 'pendingCogs',
      header: 'Pending COGS',
      Cell: ({ row }) => (
        <Checkbox checked={!!row.original.pendingCogs} disabled />
      ),
    },
    ].map((col) => {
      const isCalcColumn = col.accessorKey && col.accessorKey.includes('Calc');
      const isRtsColumn = col.accessorKey && rtsDateColumns.includes(col.accessorKey);
      const isReadOnlyDateColumn = col.accessorKey && readOnlyDateColumns.includes(col.accessorKey);
      const hasColumnDivider = col.accessorKey && dividerBeforeColumns.has(col.accessorKey);
      const isDateColumn = col.accessorKey && (
        actualDates.includes(col.accessorKey) || isReadOnlyDateColumn || isCalcColumn
      );

      const getBackgroundColor = (highlightAlpha) => {
        if (highlightedField === col.accessorKey) {
          return `rgba(255, 229, 100, ${highlightAlpha})`;
        }
        return isRtsColumn ? rtsColumnBackground : 'inherit';
      };

      return {
        ...col,
        ...(isDateColumn && { sortingFn: 'dateSort' }),
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: getBackgroundColor(0.6),
            ...(hasColumnDivider && columnDividerSx),
          }
        },
        muiTableBodyCellProps: isCalcColumn ? ({ row }) => {
          const dateValue = row.original[col.accessorKey];
          const isPast = isDateInPast(dateValue);

          const actualFieldKey = calcToActualMap[col.accessorKey];
          const actualValue = actualFieldKey ? row.original[actualFieldKey] : null;
          const hasActual = actualValue && actualValue !== '-';

          const shouldHighlightPast = isPast && !hasActual;

          return {
            sx: {
              backgroundColor: shouldHighlightPast
                ? 'rgba(255, 0, 0, 0.2)'
                : getBackgroundColor(0.3),
              color: shouldHighlightPast ? '#d32f2f' : 'inherit',
              fontWeight: shouldHighlightPast ? 'bold' : 'normal',
              ...(hasColumnDivider && columnDividerSx),
            }
          };
        } : {
          sx: {
            backgroundColor: getBackgroundColor(0.3),
            ...(hasColumnDivider && columnDividerSx),
          }
        },
      };
    });
  }, [highlightedField]);

  const sortedData = useMemo(() => {
    const getEarliestCalcDate = (row) => {
      const calcFields = calcDateFields;
      let earliestDate = null;

      calcFields.forEach(field => {
        const dateStr = row[field];
        if (dateStr && dateStr !== '-') {
          const date = parseDate(dateStr);
          if (date && (!earliestDate || date < earliestDate)) {
            earliestDate = date;
          }
        }
      });

      return earliestDate;
    };

    return [...filteredData].sort((a, b) => {
      const dateA = getEarliestCalcDate(a);
      const dateB = getEarliestCalcDate(b);

      // Null dates go to the end
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      // Sort by earliest date first
      return dateA - dateB;
    });
  }, [filteredData]);

  const table = useMaterialReactTable({
    columns,
    data: sortedData,
    getRowId: (row) => String(row._rowId),
    enableColumnPinning: true,
    sortingFns: {
      dateSort: dateSortingFn,
    },
    initialState: {
      sorting: [],
    },
    state: {
      columnPinning,
    },
    onColumnPinningChange: setColumnPinning,
  });

  return (
    <Box sx={{ px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Tabs
        value={selectedTab}
        onChange={(e, val) => {
          setSelectedTab(val);
          setSpecialFilter(null);
          setHighlightedField(null);
        }}
      >
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              All
              <Badge
                color="primary"
                badgeContent={tabCounts["All"]}
                showZero
                sx={{ "& .MuiBadge-badge": { right: -8 } }}
              />
            </Box>
          }
        />
        {statusTabs.map((label, idx) => (
          <Tab
            key={idx + 1}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {label}
                <Badge
                  color="primary"
                  badgeContent={tabCounts[label]}
                  showZero
                  sx={{ "& .MuiBadge-badge": { right: -5 } }}
                />
              </Box>
                }
              />
            ))}
          </Tabs>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ width: 180 }} size="small">
            <InputLabel>Freeze columns</InputLabel>
            <Select
              multiple
              value={frozenColumnSelection}
              onChange={handleFrozenColumnsChange}
              renderValue={(selected) =>
                selected.length === 0
                  ? 'None'
                  : `${selected.length} frozen`
              }
              label="Freeze columns"
              MenuProps={{
                PaperProps: {
                  sx: { width: 280 },
                },
              }}
            >
              {FREEZEABLE_COLUMNS.map((col) => (
                <MenuItem key={col.id} value={col.id}>
                  <Checkbox checked={frozenColumnSelection.includes(col.id)} />
                  <ListItemText primary={col.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Filter by Owner</InputLabel>
            <Select
              multiple
              value={ownerFilter}
              onChange={handleOwnerFilterChange}
              renderValue={(selected) => selected.join(', ')}
              label="Filter by Owner"
            >
              {owners.map((owner) => (
                <MenuItem key={owner} value={owner}>
                  <Checkbox checked={ownerFilter.indexOf(owner) > -1} />
                  <ListItemText primary={owner} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box mt={2}>
        <Grid container spacing={2}>
          {(statusActionButtons[statusTabs[selectedTab - 1]] || []).map((btn, idx) => (
            <Grid item key={idx}>
              <Button variant="contained" color="primary" onClick={btn.action}>{btn.label}</Button>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box mt={2}>
        <MaterialReactTable key={refreshTable} table={table} />
      </Box>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit Purchase Order</DialogTitle>
        <DialogContent>
          {editableData && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: 'primary.main', color: 'white' }}>PO</TableCell>
                      <TableCell sx={{ backgroundColor: 'primary.main', color: 'white' }}>Terms</TableCell>
                      <TableCell sx={{ backgroundColor: 'primary.main', color: 'white' }}>PO Created Date</TableCell>
                      <TableCell sx={{ backgroundColor: 'primary.main', color: 'white' }}>Vendor Target Date</TableCell>
                      <TableCell sx={{ backgroundColor: 'primary.main', color: 'white' }}>Deposit Date</TableCell>
                      <TableCell sx={{ backgroundColor: 'primary.main', color: 'white' }}>Lead Time (days)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{editableData.tranid}</TableCell>
                      <TableCell>
                        <TextField
                          select
                          value={editableData.termsText || ""}
                          onChange={(e) => handleChange("termsText", e.target.value)}
                          size="small"
                          fullWidth
                        >
                          {Object.keys(terms).map((term) => (
                            <MenuItem key={term} value={term}>{term}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>{formatDateForDisplay(editableData.createdDate)}</TableCell>
                      <TableCell>{formatDateForDisplay(editableData.vendorTargetDate)}</TableCell>
                      <TableCell>{formatDateForDisplay(editableData.depositDate)}</TableCell>
                      <TableCell>{editableData.leadTime || '-'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell rowSpan={2} sx={{ backgroundColor: 'primary.main', color: 'white' }}>Item</TableCell>
                      <TableCell rowSpan={2} sx={{ backgroundColor: 'primary.main', color: 'white' }}>Quantity</TableCell>
                      <TableCell sx={{ backgroundColor: 'primary.main', color: 'white' }}>PO Bulk Update</TableCell>
                      <TableCell rowSpan={2} sx={{ backgroundColor: 'primary.main', color: 'white' }}>Note</TableCell>
                      <TableCell rowSpan={2} sx={{ backgroundColor: 'primary.main', color: 'white' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell rowSpan={3}>{editableData.itemText}</TableCell>
                      <TableCell rowSpan={3}>
                        <TextField
                          type="number"
                          value={editableData.poQty}
                          onChange={(e) => handleChange("poQty", e.target.value)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={editableData.isRushOrder}
                          onChange={(e) => handleChange('isRushOrder', e.target.checked)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>{editableData.note}</TableCell>
                      <TableCell>{editableData.status}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {popupDateGroups.map((group, index) => (
                  <Table key={index}>
                    <TableHead>
                      <TableRow>
                        {popupTableHeaders[index].map((header, i) => (
                          <TableCell
                          key={i}
                          sx={{
                            backgroundColor: !editableData?.inboundId && ['INBOUND SHIPMENT ID', 'ACTUAL ETD', 'ACTUAL ETA PORT', 'ACTUAL LFD', 'ACTUAL 3PL ARRIVAL DATE'].includes(header)
                              ? 'grey.300'
                              : header === 'ACTUAL READY TO SHIP DATE'
                                ? rtsColumnBackground
                                : 'primary.main',
                            color: !editableData?.inboundId && ['INBOUND SHIPMENT ID', 'ACTUAL ETD', 'ACTUAL ETA PORT', 'ACTUAL LFD', 'ACTUAL 3PL ARRIVAL DATE'].includes(header) || header === 'ACTUAL READY TO SHIP DATE'
                              ? 'text.primary'
                              : 'white',
                          }}
                          >{header}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        {group.map((field, i) => (
                          <TableCell
                            key={i}
                            sx={{
                              backgroundColor:
                                highlightedField === field
                                  ? 'rgba(255, 229, 100, 0.4)'
                                  : field === 'rtsActual'
                                    ? rtsColumnBackground
                                    : 'inherit',
                            }}
                          >
                            {field === 'shipNum' ? (
                              editableData?.shipNum || ''
                            ) : field === 'rtsActual' ? (
                              formatDateForDisplay(editableData[field])
                            ) : (
                              <DatePicker
                                disabled={
                                  !editableData?.inboundId &&
                                  ['etdActual', 'etaPortActual', 'lfdPortActual', 'arrival3PLActual', 'pendingCogs'].includes(field)
                                }
                                defaultValue={
                                  editableData[field] && editableData[field] !== '-'
                                    ? parseDDMMYYYY(editableData[field])
                                    : null
                                }
                                onChange={(val) => {
                                  if (val) {
                                    handleChange(field, formatDDMMYYYY(val));
                                  } else {
                                    handleChange(field, null);
                                  }
                                }}
                                format="dd/MM/yyyy"
                                slotProps={{
                                  textField: {
                                    error: false,
                                    required: false,
                                  },
                                }}
                              />
                            )}
                          </TableCell>

                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                ))}
              </TableContainer>
            </LocalizationProvider>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={updating}>
            {updating ? <CircularProgress size={20} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default POTable;
